'use client'

import { Eye, EyeOff } from 'lucide-react'
import React, { HTMLInputTypeAttribute, useEffect, useState } from 'react'
import { useController, UseControllerProps } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'

interface Props {
  label?: string
  placeholder?: string
  type?: HTMLInputTypeAttribute
  defaultValue?: string
}

export const InputForm = React.forwardRef<
  HTMLInputElement,
  Props & UseControllerProps
>((props, ref) => {
  const {
    control,
    name,
    label,
    placeholder = 'Escriba...',
    type = 'text',
    rules,
    defaultValue,
  } = props

  const { fieldState, field } = useController({ control, name, rules })
  const isRequired = rules?.required !== undefined

  useEffect(() => {
    field.value = defaultValue ?? ''
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [showPassword, setShowPassword] = useState(false)
  const isPassword = type === 'password'

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          {label && (
            <FormLabel>
              {label}
              {isRequired && ' (*)'}
            </FormLabel>
          )}
          <FormControl>
            <div className="relative">
              <Input
                {...field}
                className={isPassword ? 'pr-10' : ''}
                placeholder={placeholder}
                ref={ref}
                type={isPassword ? (showPassword ? 'text' : 'password') : type}
                value={field.value ?? ''}
              />
              {isPassword && (
                <Button
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-2"
                  onClick={() => setShowPassword((prev) => !prev)}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
          </FormControl>
          <FormMessage>{fieldState.error?.message}</FormMessage>
        </FormItem>
      )}
    />
  )
})

InputForm.displayName = 'InputForm'
